import React from "react";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsPageHeader from "../clients/Tools/kit/ToolsPageHeader";
import ToolsTabs from "../clients/Tools/kit/ToolsTabs";
import PlaybookBuildService from "../../services/PlaybookBuildService";
import PlaybookRunService from "../../services/PlaybookRunService";
import PlaybookScriptService from "../../services/PlaybookScriptService";
import SkillBuildService from "../../services/SkillBuildService";
import UtilityService from "../../services/UtilityService";
import BuildProgressService from "../../services/BuildProgressService";
import BuildPageSkeleton from "./BuildPageSkeleton";
import BuildStageTracker from "./BuildStageTracker";
import PlaybookBuildConversationPanel from "./PlaybookBuildConversationPanel";
import PlaybookBuildDraftCard from "./PlaybookBuildDraftCard";
import PlaybookBuildGaps from "./PlaybookBuildGaps";
import PlaybookBuildInputsCard from "./PlaybookBuildInputsCard";
import PlaybookBuildPreview from "./PlaybookBuildPreview";
import PlaybookBuildSourcesCard from "./PlaybookBuildSourcesCard";
import PlaybookBuildSkillsCard from "./PlaybookBuildSkillsCard";
import PlaybookBuildScriptsCard from "./PlaybookBuildScriptsCard";
import PlaybookBuildTodoList from "./PlaybookBuildTodoList";
import PlaybookScriptDetailModal from "./PlaybookScriptDetailModal";
import PlaybookScriptLibraryModal from "./PlaybookScriptLibraryModal";

import "./build-page.css";
import "./playbook-build-page.css";
import "./playbook-build-panel.css";
const NEW_SKILL_ROUTE_ID = "new";

const POLL_WHILE_TESTING_MILLISECONDS = 4000;

class PlaybookBuildPage extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			session: null,
			loadError: null,
			isSending: false,
			isBuilding: false,
			isPublishing: false,
			actionError: null,
			isUploading: false,
			isSavingSources: false,
			isSavingLines: false,
			isSavingGaps: false,
			scripts: null,
			scriptsViewer: null,
			firmScripts: [],
			scriptBusyKey: null,
			scriptWriteStartedAt: null,
			waitStartedAt: null,
			waitKind: null,
			openPinId: null,
			libraryGapId: undefined,
			activeTab: null,
		};
		this.pollTimer = null;
		this.composerRef = React.createRef();
		this.paneByTab = {};
	}

	componentDidMount() {
		Promise.all([this.fetchSession(), this.fetchScripts()]).then(async () => {
			const { session, scripts, scriptsViewer, activeTab } = this.state;
			const isFirstTabUndecided = !!session && activeTab === null;
			if (isFirstTabUndecided) {
				const nextStep = PlaybookBuildService.describeNextStep({ session, scripts, viewer: scriptsViewer });
				await this.update({ activeTab: nextStep.tab || "playbook" });
			}
			await this.tellGuideAboutAddedSkill();
		});
	}

	tellGuideAboutAddedSkill = async () => {
		const { addedFromDraft, history, sessionId } = this.props;
		if (!addedFromDraft || !this.state.session) return;
		history.replace({ pathname: `/playbooks/build/${sessionId}`, state: {} });
		const isLinked = await this.act({
			busyKey: "isSending",
			action: () => PlaybookBuildService.linkDraftedSkill({ sessionId, draftId: addedFromDraft.draftId, skillId: addedFromDraft.skillId }),
		});
		if (!isLinked) return;
		const addedMessage = this.state.session.messages.find(message => message.draftSkill && message.draftSkill.draftId === addedFromDraft.draftId);
		await this.sendText({ text: `I added the skill "${addedMessage.draftSkill.addedSkill.name}" to the firm's skills. Use it in this playbook.` });
	};

	componentWillUnmount() {
		if (this.pollTimer) clearTimeout(this.pollTimer);
	}

	fetchSession = async () => {
		const { sessionId } = this.props;
		try {
			const session = await PlaybookBuildService.getBuild({ sessionId });
			await this.update({ session, loadError: null });
			this.scheduleRefreshWhileTesting({ session });
		} catch (error) {
			await this.update({ loadError: UtilityService.describeRequestError({ error, fallback: "This conversation could not be loaded." }) });
		}
	};

	scheduleRefreshWhileTesting = ({ session }) => {
		if (this.pollTimer) clearTimeout(this.pollTimer);
		const isTestRunning = session.testRuns.some(run => !PlaybookRunService.isFinished({ status: run.status }));
		if (isTestRunning) this.pollTimer = setTimeout(this.fetchSession, POLL_WHILE_TESTING_MILLISECONDS);
	};

	act = async ({ busyKey, action }) => {
		await this.update({ [busyKey]: true, actionError: null });
		try {
			const session = await action();
			await this.update({ session, [busyKey]: false });
			this.scheduleRefreshWhileTesting({ session });
			return true;
		} catch (error) {
			await this.update({
				[busyKey]: false,
				actionError: UtilityService.describeRequestError({ error, fallback: error.message || "That did not work. Try again." }),
			});
			return false;
		}
	};

	fetchScripts = async () => {
		const { sessionId } = this.props;
		try {
			const { scripts, viewer } = await PlaybookScriptService.listBuildScripts({ sessionId });
			const firmScripts = viewer.areScriptsOn ? await PlaybookScriptService.listScriptLibrary() : [];
			await this.update({ scripts, scriptsViewer: viewer, firmScripts });
		} catch (error) {
			await this.update({ actionError: UtilityService.describeRequestError({ error, fallback: "This conversation's scripts could not be loaded." }) });
		}
	};

	runScriptAction = async ({ busyKey, action, pinId, gapId }) => {
		await this.update({ scriptBusyKey: busyKey, actionError: null });
		let answer = null;
		try {
			answer = await action();
		} catch (error) {
			const refusal = UtilityService.describeRequestError({ error, fallback: error.message || "That did not work. Try again." });
			await this.fetchScripts();
			const isScriptModalClosedMeanwhile = pinId !== undefined && this.state.openPinId !== pinId;
			const isLibraryClosedMeanwhile = gapId !== undefined && this.state.libraryGapId !== gapId;
			const isRefusalUnseen = isScriptModalClosedMeanwhile || isLibraryClosedMeanwhile;
			const actionError = isRefusalUnseen ? refusal : this.state.actionError;
			await this.update({ scriptBusyKey: null, actionError });
			return { answer: null, refusal };
		}
		await this.fetchSession();
		await this.fetchScripts();
		await this.update({ scriptBusyKey: null });
		return { answer, refusal: null };
	};

	onWriteScript = async ({ gapId, scriptKey }) => {
		const { sessionId } = this.props;
		const writeScript = () => PlaybookScriptService.writeBuildScript({ sessionId, gapId, scriptKey: scriptKey || null });
		await this.update({ scriptWriteStartedAt: Date.now() });
		const { answer, refusal } = await this.runScriptAction({ busyKey: `writeScript-${gapId}`, action: writeScript });
		await this.update({ scriptWriteStartedAt: null });
		if (refusal) {
			await this.update({ actionError: refusal });
			return;
		}
		const scripts = this.state.scripts || [];
		const writtenScript = scripts.find(script => script.scriptId === answer.scriptId && script.isOnItsGap);
		if (writtenScript) await this.update({ openPinId: writtenScript.pinId });
	};

	onUseScriptAgain = async ({ gapId }) => {
		const { sessionId } = this.props;
		const scripts = this.state.scripts || [];
		const keptScripts = scripts.filter(script => !script.isOnItsGap && String(script.gapId) === String(gapId));
		const keptScriptsNewestFirst = [...keptScripts].sort((first, second) => second.pinId - first.pinId);
		const keptScript = keptScriptsNewestFirst[0];
		if (!keptScript) return;
		const { pinId, code, manifest, plainDescription } = keptScript;
		const putScriptBack = () => PlaybookScriptService.editBuildScript({ sessionId, pinId, code, manifest, plainDescription });
		const { refusal } = await this.runScriptAction({ busyKey: `useScriptAgain-${gapId}`, action: putScriptBack });
		if (refusal) await this.update({ actionError: refusal });
	};

	onOpenLibrary = ({ gapId }) => {
		this.update({ libraryGapId: gapId });
	};

	onOpenScript = ({ pinId }) => {
		this.update({ openPinId: pinId });
	};

	showActionError = ({ message }) => {
		this.update({ actionError: message });
	};

	sendText = async ({ text }) => {
		const { sessionId } = this.props;
		const pendingSession = { ...this.state.session, messages: [...this.state.session.messages, { from: "person", text, at: new Date().toISOString() }] };
		const waitStartedAt = Date.now();
		await this.update({ session: pendingSession, waitStartedAt, waitKind: "message" });
		const isSent = await this.act({ busyKey: "isSending", action: () => PlaybookBuildService.sendBuildMessage({ sessionId, text }) });
		if (this.state.waitStartedAt === waitStartedAt) await this.update({ waitStartedAt: null, waitKind: null });
		if (!isSent) await this.update({ session: { ...pendingSession, messages: pendingSession.messages.slice(0, -1) } });
		return isSent;
	};

	onAddDraftSkill = ({ draftSkill }) => {
		const { history, sessionId } = this.props;
		const prefill = SkillBuildService.skillDraftForEditor({ session: { ...this.state.session, draftSkill } });
		history.push({
			pathname: `/playbooks/skill/${NEW_SKILL_ROUTE_ID}`,
			state: { prefill, returnTo: `/playbooks/build/${sessionId}`, draftId: draftSkill.draftId },
		});
	};

	onDeclineDraftSkill = ({ draftSkill }) => {
		this.sendText({ text: `No skill for "${draftSkill.name}" — leave it out and don't draft it again.` });
	};

	onImportPrompt = async ({ prompt }) => {
		const { sessionId } = this.props;
		const waitStartedAt = Date.now();
		await this.update({ waitStartedAt, waitKind: "import" });
		const isImported = await this.act({ busyKey: "isSending", action: () => PlaybookBuildService.importBuildPrompt({ sessionId, prompt }) });
		if (this.state.waitStartedAt === waitStartedAt) await this.update({ waitStartedAt: null, waitKind: null });
		return isImported;
	};

	onUploadFiles = ({ files }) => {
		const { sessionId } = this.props;
		this.act({ busyKey: "isUploading", action: () => PlaybookBuildService.uploadReferenceFiles({ sessionId, files }) });
	};

	onRemoveFile = ({ fileId }) => {
		const { sessionId } = this.props;
		this.act({ busyKey: "isUploading", action: () => PlaybookBuildService.removeReferenceFile({ sessionId, fileId }) });
	};

	saveSources = ({ sources }) => {
		const { sessionId } = this.props;
		this.act({ busyKey: "isSavingSources", action: () => PlaybookBuildService.setBuildSources({ sessionId, sources }) });
	};

	onAddSource = ({ key, isRequired }) => {
		const { session } = this.state;
		this.saveSources({ sources: [...session.sources, { key, isRequired }] });
	};

	onSetSourceNeed = ({ key, isRequired }) => {
		const { session } = this.state;
		this.saveSources({ sources: session.sources.map(source => (source.key === key ? { key, isRequired } : source)) });
	};

	onRemoveSource = ({ key }) => {
		const { session } = this.state;
		this.saveSources({ sources: session.sources.filter(source => source.key !== key) });
	};

	onSetSkillRemoved = ({ skillId, isRemoved }) => {
		const { sessionId } = this.props;
		this.act({ busyKey: "isSavingSources", action: () => PlaybookBuildService.setBuildSkillRemoved({ sessionId, skillId, isRemoved }) });
	};

	onBuild = async () => {
		const { sessionId } = this.props;
		const waitStartedAt = Date.now();
		await this.update({ waitStartedAt, waitKind: "build" });
		await this.act({ busyKey: "isBuilding", action: () => PlaybookBuildService.buildDraft({ sessionId }) });
		if (this.state.waitStartedAt === waitStartedAt) await this.update({ waitStartedAt: null, waitKind: null });
	};

	onWaiveLine = ({ canonicalCode, reason }) => {
		const { sessionId } = this.props;
		return this.act({ busyKey: "isSavingLines", action: () => PlaybookBuildService.waiveDraftLine({ sessionId, canonicalCode, reason }) });
	};

	onClearLineWaiver = ({ canonicalCode }) => {
		const { sessionId } = this.props;
		this.act({ busyKey: "isSavingLines", action: () => PlaybookBuildService.clearDraftLineWaiver({ sessionId, canonicalCode }) });
	};

	onWaiveGap = ({ gapId, reason }) => {
		const { sessionId } = this.props;
		return this.act({ busyKey: "isSavingGaps", action: () => PlaybookBuildService.waiveBuildGap({ sessionId, gapId, reason }) });
	};

	onDropGap = ({ gapId }) => {
		const { sessionId } = this.props;
		this.act({ busyKey: "isSavingGaps", action: () => PlaybookBuildService.dropBuildGap({ sessionId, gapId }) });
	};

	onReopenGap = async ({ gapId }) => {
		const { sessionId } = this.props;
		const isReopened = await this.act({ busyKey: "isSavingGaps", action: () => PlaybookBuildService.reopenBuildGap({ sessionId, gapId }) });
		if (isReopened) await this.fetchScripts();
	};

	onPublish = () => {
		const { sessionId } = this.props;
		this.act({ busyKey: "isPublishing", action: () => PlaybookBuildService.publishDraft({ sessionId }) });
	};

	onStartTest = async ({ clientId, inputs }) => {
		const { sessionId } = this.props;
		try {
			const result = await PlaybookBuildService.testDraft({ sessionId, clientId, inputs });
			await this.update({ session: result.session, actionError: null });
			this.scheduleRefreshWhileTesting({ session: result.session });
		} catch (error) {
			await this.update({ actionError: UtilityService.describeRequestError({ error, fallback: "The test did not start." }) });
		}
	};

	onSelectTab = tab => {
		this.update({ activeTab: tab });
	};

	onNextStep = async ({ step }) => {
		const { action, tab, key } = step;
		const { session, isSending, isBuilding, isPublishing } = this.state;
		const composer = this.composerRef.current;
		const isBuildOff = !session.isReadyToBuild || isBuilding || isSending;
		const isPublishOff = !session.canPublish || isPublishing;
		const isReplyOff = isSending || isBuilding;
		if (action === "build" && isBuildOff) return;
		if (action === "publish" && isPublishOff) return;
		if (action === "reply" && isReplyOff) return;
		if (action === "build") this.onBuild();
		if (action === "publish") this.onPublish();
		if (action === "reply" && composer) composer.focus();
		const isTabOnlyStep = !action && !!tab;
		if (!isTabOnlyStep) return;
		await this.update({ activeTab: tab });
		const pane = this.paneByTab[tab];
		if (!pane) return;
		pane.focus();
		const isTodoTab = tab === "todo";
		const isScriptStep = isTodoTab && !!key && key.startsWith("script-");
		const isGapsStep = isTodoTab && key === "openGaps";
		const scriptsCard = isScriptStep ? pane.querySelector(".playbook-build-scripts") : null;
		const gapsList = isGapsStep ? pane.querySelector(".playbook-build-gaps__list") : null;
		const gapsCard = gapsList ? gapsList.closest(".build-card") : null;
		const stepCard = scriptsCard || gapsCard;
		if (stepCard) stepCard.scrollIntoView({ block: "start" });
	};

	renderHeader({ nextStep, busyLabels, waitLabel, tabs }) {
		const { onBack } = this.props;
		const { session, isSending, isBuilding, isPublishing } = this.state;
		const { draft } = session;
		const title = draft ? draft.name : session.brief.name || "New playbook";
		const badges = draft ? <span className="tk-badge tk-badge--accent">Draft {draft.versionNumber}</span> : null;
		const isChangingAPublishedPlaybook = !!session.playbookId && !draft && session.isChangingAPublishedAgent;
		const subtitle = isChangingAPublishedPlaybook ? "Changing a published playbook — it keeps running as it is until you publish." : null;
		const stages = PlaybookBuildService.describeBuildStages({ session });

		const isBuildStep = nextStep.action === "build";
		const isPublishStep = nextStep.action === "publish";
		const isWaiting = !!waitLabel;
		const isButtonStep = (isBuildStep || isPublishStep) && !isWaiting;
		const isReplyLinkShown = nextStep.action === "reply" && !isSending && !isBuilding;
		const isTabLinkShown = !nextStep.action && !!nextStep.tab;
		const isActionableStep = !!nextStep.action || !!nextStep.tab;
		const stepTab = isTabLinkShown ? tabs.find(tab => tab.key === nextStep.tab) : null;

		const buildLabel = draft ? "Build again" : "Build the draft";
		const publishLabel = draft ? `Publish version ${draft.versionNumber}` : "Publish";
		const buttonLabel = isBuildStep ? buildLabel : publishLabel;
		const busyLabel = isBuildStep ? busyLabels.build : busyLabels.publish;
		const isButtonBusy = isBuildStep ? isBuilding : isPublishing;
		const isBuildOff = !session.isReadyToBuild || isBuilding;
		const isPublishOff = !session.canPublish || isPublishing;
		const isButtonOff = isBuildStep ? isBuildOff : isPublishOff;
		const isStepSaidByButton = isButtonStep && nextStep.text === buttonLabel;

		let stepVerb = null;
		if (isReplyLinkShown) stepVerb = "Reply to the guide";
		if (stepTab) stepVerb = `Open ${stepTab.label}`;
		const stepVerbLink = stepVerb ? (
			<button type="button" className="tk-text-action" onClick={() => this.onNextStep({ step: nextStep })}>
				{stepVerb}
			</button>
		) : null;

		let facts = (
			<span className="playbook-build__next">
				<span className="tk-muted">Next:</span> {nextStep.text} {stepVerbLink}
			</span>
		);
		if (!isActionableStep) facts = <span>{nextStep.text}</span>;
		if (isStepSaidByButton) facts = null;
		if (isWaiting) facts = <span>{waitLabel}</span>;

		const actions = isButtonStep ? (
			<button type="button" className="tk-button tk-button--primary" onClick={() => this.onNextStep({ step: nextStep })} disabled={isButtonOff}>
				{isBuildStep ? <Icon.Box size={14} /> : <Icon.UploadCloud size={14} />}
				{isButtonBusy ? busyLabel : buttonLabel}
			</button>
		) : null;

		return (
			<ToolsPageHeader
				compact
				onBack={onBack}
				backLabel="Back to playbooks"
				lead={
					<span className="tk-icon-tile tk-icon-tile--lg">
						<Icon.Edit3 />
					</span>
				}
				title={title}
				badges={badges}
				subtitle={subtitle}
				facts={facts}
				actions={actions}
				nav={<BuildStageTracker stages={stages} />}
			/>
		);
	}

	renderSidePanel({ tabs, todos, areScriptsOn, gapIdsWithAKeptScript, whyNotWriteAgainFor, keptScriptIdFor, isConversationOpen, busyLabels }) {
		const { session, activeTab, isSending, isBuilding, isPublishing, isUploading, isSavingSources, isSavingLines, isSavingGaps } = this.state;
		const { scripts, scriptsViewer, firmScripts, scriptBusyKey, scriptWriteStartedAt } = this.state;
		const areScriptsLoaded = scripts !== null && scriptsViewer !== null;
		const shownTab = activeTab || "playbook";

		const paneContentByTab = {
			playbook: <PlaybookBuildPreview preview={PlaybookBuildService.describePlaybookPreview({ session })} />,
			todo: (
				<React.Fragment>
					<PlaybookBuildTodoList
						todos={todos}
						isSending={isSending}
						isBuilding={isBuilding}
						isPublishing={isPublishing}
						busyLabels={busyLabels}
						onChoose={({ todo }) => this.onNextStep({ step: todo })}
					/>
					<PlaybookBuildGaps
						gaps={session.gaps}
						isOpen={isConversationOpen}
						isSaving={isSavingGaps || isSending || isBuilding}
						onWaive={this.onWaiveGap}
						onDrop={this.onDropGap}
						onReopen={this.onReopenGap}
						areScriptsOn={areScriptsOn}
						hasFirmScripts={firmScripts.length > 0}
						gapIdsWithAKeptScript={gapIdsWithAKeptScript}
						whyNotWriteAgainFor={whyNotWriteAgainFor}
						keptScriptIdFor={keptScriptIdFor}
						scriptBusyKey={scriptBusyKey}
						scriptWriteStartedAt={scriptWriteStartedAt}
						onWriteScript={this.onWriteScript}
						onUseScriptAgain={this.onUseScriptAgain}
						onOpenLibrary={this.onOpenLibrary}
					/>
					{areScriptsLoaded && <PlaybookBuildScriptsCard scripts={scripts} viewer={scriptsViewer} isOpen={isConversationOpen} onOpenScript={this.onOpenScript} />}
				</React.Fragment>
			),
			draft: (
				<React.Fragment>
					<PlaybookBuildDraftCard
						session={session}
						isSending={isSending}
						isBuilding={isBuilding}
						isPublishing={isPublishing}
						onBuild={this.onBuild}
						onPublish={this.onPublish}
						isSavingLines={isSavingLines}
						onWaiveLine={this.onWaiveLine}
						onClearLineWaiver={this.onClearLineWaiver}
						onStartTest={this.onStartTest}
						onError={this.showActionError}
					/>
					<PlaybookBuildInputsCard session={session} />
				</React.Fragment>
			),
			sources: (
				<React.Fragment>
					<PlaybookBuildSourcesCard
						session={session}
						isUploading={isUploading}
						isSavingSources={isSavingSources}
						onAddSource={this.onAddSource}
						onSetSourceNeed={this.onSetSourceNeed}
						onRemoveSource={this.onRemoveSource}
						onUploadFiles={this.onUploadFiles}
						onRemoveFile={this.onRemoveFile}
					/>
					<PlaybookBuildSkillsCard session={session} onSetSkillRemoved={this.onSetSkillRemoved} />
				</React.Fragment>
			),
		};

		return (
			<div className="build-page__side playbook-build-panel">
				<ToolsTabs tabs={tabs} activeKey={shownTab} onSelect={this.onSelectTab} />
				{tabs.map(tab => (
					<div
						key={tab.key}
						ref={pane => (this.paneByTab[tab.key] = pane)}
						className="playbook-build-panel__pane"
						role="tabpanel"
						aria-label={tab.label}
						tabIndex={-1}
						hidden={shownTab !== tab.key}
					>
						{paneContentByTab[tab.key]}
					</div>
				))}
			</div>
		);
	}

	render() {
		const { sessionId } = this.props;
		const { session, loadError, actionError, isSending, isBuilding, waitStartedAt, waitKind, activeTab } = this.state;
		const { scripts, scriptsViewer, firmScripts, scriptBusyKey, openPinId, libraryGapId } = this.state;
		if (loadError) return <div className="tk-notice tk-notice--danger">{loadError}</div>;
		if (!session || activeTab === null) return <BuildPageSkeleton hasPanelTabs />;
		const areScriptsOn = scriptsViewer ? scriptsViewer.areScriptsOn : false;
		const loadedScripts = scripts || [];
		const openGapIds = session.gaps.filter(gap => gap.status === "open").map(gap => String(gap.id));
		const keptScripts = loadedScripts.filter(script => !script.isOnItsGap && openGapIds.includes(String(script.gapId)));
		const gapIdsWithAKeptScript = keptScripts.map(script => String(script.gapId));
		const keptScriptsOldestFirst = [...keptScripts].sort((first, second) => first.pinId - second.pinId);
		const whyNotWriteAgainFor = {};
		const keptScriptIdFor = {};
		keptScriptsOldestFirst.forEach(script => {
			whyNotWriteAgainFor[String(script.gapId)] = script.whyNotRedraft;
			keptScriptIdFor[String(script.gapId)] = script.scriptId;
		});
		const openScript = loadedScripts.find(script => script.pinId === openPinId) || null;
		const isLibraryOpen = libraryGapId !== undefined;
		const isConversationOpen = session.status === "open";
		const todos = PlaybookBuildService.describeBuildTodos({ session, scripts, viewer: scriptsViewer });
		const nextStep = PlaybookBuildService.describeNextStep({ session, scripts, viewer: scriptsViewer });
		const { draft } = session;
		let buildingLabel = "Building the draft…";
		if (!session.playbookId) buildingLabel = "Building draft 1…";
		if (draft) buildingLabel = `Building draft ${draft.versionNumber + 1}…`;
		const busyLabels = { build: buildingLabel, publish: "Publishing…" };
		const blockingTodos = todos.filter(todo => !todo.isOptional);
		const isAGapWaiting = blockingTodos.some(todo => todo.key === "openGaps");
		const todoCount = blockingTodos.length > 0 ? blockingTodos.length : null;
		const suggestedSourceCount = session.suggestedSources.length > 0 ? session.suggestedSources.length : null;
		const tabs = [
			{ key: "playbook", label: "Playbook" },
			{ key: "todo", label: "To do", count: todoCount, countTone: isAGapWaiting ? "warning" : null },
			{ key: "draft", label: "Draft" },
			{ key: "sources", label: "Sources", count: suggestedSourceCount },
		];

		const isBuildWaiting = waitKind === "build" && isBuilding;
		const isGuideWaiting = (waitKind === "message" || waitKind === "import") && isSending;
		let progress = null;
		if (isGuideWaiting) {
			const isPastedPrompt = waitKind === "import";
			const steps = BuildProgressService.playbookTurnSteps({ session, areScriptsOn, isPastedPrompt });
			const slowAfterSeconds = BuildProgressService.PLAYBOOK_TURN_SLOW_AFTER_SECONDS;
			const longestSeconds = BuildProgressService.PLAYBOOK_TURN_LONGEST_SECONDS;
			progress = { steps, slowAfterSeconds, startedAt: waitStartedAt, longestSeconds };
		}
		if (isBuildWaiting) {
			const steps = BuildProgressService.draftBuildSteps({ session });
			const slowAfterSeconds = BuildProgressService.DRAFT_BUILD_SLOW_AFTER_SECONDS;
			const longestSeconds = BuildProgressService.DRAFT_BUILD_LONGEST_SECONDS;
			progress = { steps, slowAfterSeconds, startedAt: waitStartedAt, longestSeconds };
		}
		const lastStep = progress ? progress.steps[progress.steps.length - 1] : null;
		let waitLabel = null;
		if (isGuideWaiting) waitLabel = `${lastStep.label}…`;
		if (isBuildWaiting) waitLabel = busyLabels.build;

		return (
			<div className="build-page">
				{this.renderHeader({ nextStep, busyLabels, waitLabel, tabs })}
				{actionError && (
					<div className="tk-notice tk-notice--danger">
						<div className="tk-notice__title">
							<Icon.AlertTriangle size={14} />
							{actionError}
						</div>
					</div>
				)}
				<div className="build-page__layout">
					<PlaybookBuildConversationPanel
						session={session}
						isSending={isSending || isBuilding}
						progress={progress}
						composerRef={this.composerRef}
						onSendText={this.sendText}
						onImportPrompt={this.onImportPrompt}
						onAddDraftSkill={this.onAddDraftSkill}
						onDeclineDraftSkill={this.onDeclineDraftSkill}
						hasWelcome
					/>
					{this.renderSidePanel({ tabs, todos, areScriptsOn, gapIdsWithAKeptScript, whyNotWriteAgainFor, keptScriptIdFor, isConversationOpen, busyLabels })}
				</div>
				<PlaybookScriptDetailModal
					key={`script-${openPinId}`}
					script={openScript}
					viewer={scriptsViewer}
					sessionId={sessionId}
					isOpen={Boolean(openScript)}
					busyKey={scriptBusyKey}
					isConversationOpen={isConversationOpen}
					onClose={() => this.update({ openPinId: null })}
					runScriptAction={this.runScriptAction}
				/>
				{isLibraryOpen && (
					<PlaybookScriptLibraryModal
						key={`library-${libraryGapId}`}
						isOpen={isLibraryOpen}
						gapId={libraryGapId}
						sessionId={sessionId}
						firmScripts={firmScripts}
						viewer={scriptsViewer}
						busyKey={scriptBusyKey}
						isConversationOpen={isConversationOpen}
						holdsAKeptScript={gapIdsWithAKeptScript.includes(String(libraryGapId))}
						onClose={() => this.update({ libraryGapId: undefined })}
						runScriptAction={this.runScriptAction}
					/>
				)}
			</div>
		);
	}
}

export default PlaybookBuildPage;
